/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for
 * license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is
 * regenerated.
 */
import { __assign } from "tslib";
import * as msRest from "@azure/ms-rest-js";
import * as Mappers from "../models/virtualMachinesMappers";
import * as Parameters from "../models/parameters";
/** Class representing a VirtualMachines. */
var VirtualMachines = /** @class */function () {
  /**
   * Create a VirtualMachines.
   * @param {LabServicesClientContext} client Reference to the service client.
   */
  function VirtualMachines(client) {
    this.client = client;
  }
  VirtualMachines.prototype.listByLab = function (resourceGroupName, labName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, listByLabOperationSpec, callback);
  };
  VirtualMachines.prototype.get = function (resourceGroupName, labName, virtualMachineName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, getOperationSpec, callback);
  };
  /**
   * Action to start a lab virtual machine.
   * @summary Start a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  VirtualMachines.prototype.start = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.beginStart(resourceGroupName, labName, virtualMachineName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Action to stop a lab virtual machine.
   * @summary Stop a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  VirtualMachines.prototype.stop = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.beginStop(resourceGroupName, labName, virtualMachineName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Re-image a lab virtual machine. The virtual machine will be deleted and recreated using the
   * latest published snapshot of the reference environment of the lab.
   * @summary Re-image a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  VirtualMachines.prototype.reimage = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.beginReimage(resourceGroupName, labName, virtualMachineName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Action to redeploy a lab virtual machine to a different compute node. For troubleshooting
   * connectivity.
   * @summary Redeploy a lab virtual machine to a different compute node. For troubleshooting
   * connectivity.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  VirtualMachines.prototype.redeploy = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.beginRedeploy(resourceGroupName, labName, virtualMachineName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Resets a lab virtual machine password.
   * @summary Reset a lab virtual machine password.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  VirtualMachines.prototype.resetPassword = function (body, resourceGroupName, labName, virtualMachineName, options) {
    return this.beginResetPassword(body, resourceGroupName, labName, virtualMachineName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Action to start a lab virtual machine.
   * @summary Start a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  VirtualMachines.prototype.beginStart = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, beginStartOperationSpec, options);
  };
  /**
   * Action to stop a lab virtual machine.
   * @summary Stop a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  VirtualMachines.prototype.beginStop = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, beginStopOperationSpec, options);
  };
  /**
   * Re-image a lab virtual machine. The virtual machine will be deleted and recreated using the
   * latest published snapshot of the reference environment of the lab.
   * @summary Re-image a lab virtual machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  VirtualMachines.prototype.beginReimage = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, beginReimageOperationSpec, options);
  };
  /**
   * Action to redeploy a lab virtual machine to a different compute node. For troubleshooting
   * connectivity.
   * @summary Redeploy a lab virtual machine to a different compute node. For troubleshooting
   * connectivity.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  VirtualMachines.prototype.beginRedeploy = function (resourceGroupName, labName, virtualMachineName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, beginRedeployOperationSpec, options);
  };
  /**
   * Resets a lab virtual machine password.
   * @summary Reset a lab virtual machine password.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param virtualMachineName The ID of the virtual machine that uniquely identifies it within the
   * containing lab. Used in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  VirtualMachines.prototype.beginResetPassword = function (body, resourceGroupName, labName, virtualMachineName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labName: labName,
      virtualMachineName: virtualMachineName,
      options: options
    }, beginResetPasswordOperationSpec, options);
  };
  VirtualMachines.prototype.listByLabNext = function (nextPageLink, options, callback) {
    return this.client.sendOperationRequest({
      nextPageLink: nextPageLink,
      options: options
    }, listByLabNextOperationSpec, callback);
  };
  return VirtualMachines;
}();
export { VirtualMachines };
// Operation Specifications
var serializer = new msRest.Serializer(Mappers);
var listByLabOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion, Parameters.filter],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedVirtualMachines
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var getOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.VirtualMachine
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginStartOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}/start",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginStopOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}/stop",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginReimageOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}/reimage",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginRedeployOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}/redeploy",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginResetPasswordOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/virtualMachines/{virtualMachineName}/resetPassword",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName, Parameters.virtualMachineName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.ResetPasswordBody), {
      required: true
    })
  },
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listByLabNextOperationSpec = {
  httpMethod: "GET",
  baseUrl: "https://management.azure.com",
  path: "{nextLink}",
  urlParameters: [Parameters.nextPageLink],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedVirtualMachines
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};